import { getLastFromPath, makeId, Spacer } from '@oy/uidesktop';
import {
  IconName,
  ProductFeatureIcon,
  ProductFeatureIconList,
  ProductFeatureIconName,
  SystemIcon,
} from '@oy/uiicon';
import classNames from 'classnames';
import React, { ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { PATH } from 'src/constants/Path';
import { useTranslate } from 'src/hooks';
import { menuItemIconHiddenList } from '../../constants';
import styles from '../Sidebar.module.css';
import { useMenuItemImpl } from './useMenuItemImpl';

type MenuItemProps = {
  title?: string;
  titleAdditionalComponent?: ReactNode;
  path: string;
  icon?: string;
  onClick?:
    | null
    | ((e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void);
  className?: string;
  analyticEvent?: string;
};

const MenuItem = ({
  title,
  titleAdditionalComponent,
  path,
  icon,
  onClick,
  className,
  analyticEvent,
}: MenuItemProps) => {
  const location = useLocation();
  const { getTranslation } = useTranslate();

  const {
    getIconColor,
    trackMenuItemSelection,
    setIsHover,
    isNotificationTooltipOpen,
  } = useMenuItemImpl();

  const isNoIcon = menuItemIconHiddenList.includes(title ?? '');
  const isIconAvailable =
    icon ||
    ProductFeatureIconList.includes(
      path.substring(1) as ProductFeatureIconName,
    );

  const getMenuClassName = ({ isActive }: { isActive: boolean }) => {
    if (isNotificationTooltipOpen) {
      if (path !== PATH.notifications) return '';
      return styles.active;
    }
    if (!isActive) return '';
    return styles.active;
  };

  return (
    <div
      id={makeId('menu', getLastFromPath(path))}
      className={classNames(styles.navItem, className || '')}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      data-tooltip-id="menu-tooltip"
      data-tooltip-content={title?.toString()}
      data-tooltip-place="right"
    >
      <NavLink
        className={getMenuClassName}
        to={path}
        onClick={e => {
          if (onClick) {
            onClick(e);
          }
          trackMenuItemSelection(path, analyticEvent);
        }}
      >
        {!isNoIcon && isIconAvailable && (
          <Spacer right={Spacer.Size.XS}>
            {icon ? (
              <SystemIcon
                name={icon as IconName}
                color={getIconColor(path, location)}
                size={24}
              />
            ) : (
              <ProductFeatureIcon
                name={path.substring(1) as ProductFeatureIconName}
                color={getIconColor(path, location)}
                size={24}
              />
            )}
          </Spacer>
        )}

        <div className={styles.menuTitle}>
          <Spacer tag="span">{title && getTranslation(title)}</Spacer>
          {titleAdditionalComponent}
        </div>
      </NavLink>
    </div>
  );
};

export default MenuItem;
